import { Box, InputAdornment } from "@mui/material";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { EmailOutlined } from "@mui/icons-material";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import i18next from "i18next";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { usePageTitle } from "@/utils/usePageTitle";
import { useHasPermissions } from "@/utils/useHasPermissions";
import { PermissionRoles } from "@/enum";

import { languages } from "@/constants";

import { UserLanguage } from "@/enum/user.enum";

import { ButtonColor } from "@/components/Button/types";

import { Button } from "@/components/Button/Button";

import { useUpdateMeMutation } from "../../redux/account/account.api";
import { ChangePasswordModal } from "./components/ChangePasswordModal";
import FormTextField from "./components/FormTextField";
import { UpdateMyProfileInformation, UpdateMyProfileInformationSchema } from "./schema/account";
import LanguageSelect from "./components/LanguageSelect";
import { ProfileSecurity } from "./components/ProfileSecurity";
import { McpConnections } from "./components/McpConnections";

export const Account: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.${key}`);
	const [updateMe, { isLoading }] = useUpdateMeMutation();
	const { user } = useAppSelector(authSelector);
	const { hasPermissions } = useHasPermissions();
	const isGlobalAdmin = hasPermissions([PermissionRoles.GLOBAL_ADMIN]);
	const [showPasswordModal, setShowPasswordModal] = useState(false);
	const initialValues: UpdateMyProfileInformation = useMemo(
		() => ({
			firstName: user?.firstName ?? "",
			lastName: user?.lastName ?? "",
			email: user?.email ?? "",
			userLanguage: user?.userLanguage ?? UserLanguage.DE,
		}),

		[user]
	);

	usePageTitle(ts("title"));

	const {
		register,
		handleSubmit,
		setValue,
		reset,
		formState: { errors, isDirty },
		watch,
	} = useForm<UpdateMyProfileInformation>({
		defaultValues: initialValues,
		resolver: zodResolver(UpdateMyProfileInformationSchema),
	});

	const formValues = watch();

	useEffect(() => {
		reset(initialValues, { keepDefaultValues: false });
	}, [initialValues]);

	function handleLanguageChange(value: UserLanguage) {
		setValue("userLanguage", value, {
			shouldDirty: true,
		});
	}

	const onSubmit = async (values: UpdateMyProfileInformation) => {
		const body = {} as Partial<UpdateMyProfileInformation>;
		const { ...info } = values;

		for (const key in info) {
			const initialValue = initialValues[key as keyof UpdateMyProfileInformation];
			const currentValue = values[key as keyof UpdateMyProfileInformation];

			if (currentValue !== initialValue) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(body as any)[key] = currentValue;
			}
		}

		try {
			if (Object.entries(body).length > 0) {
				const response = await updateMe({ ...body }).unwrap();

				i18next.changeLanguage(response.userLanguage.toLowerCase());
			}
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<>
			<Box
				noValidate
				autoComplete="off"
				component="form"
				sx={{
					paddingTop: "24px",
					paddingBottom: "16px",
					gap: "12px",
					maxWidth: 640,
					borderWidth: "1px",
					m: "32px",
					borderRadius: "12px",
					borderColor: "#DCDAD6",
					backgroundColor: "#F4F3F1",
					boxShadow: "0px 1px 2px 0px #11110F0D",
				}}
				onSubmit={handleSubmit(onSubmit)}
			>
				<Box sx={{ paddingX: "24px", mb: "12px" }}>
					<div className="flex flex-col justify-between md:flex-row md:gap-6">
						<FormTextField
							errorMessage={errors?.firstName?.message}
							formRegister={register("firstName")}
							label={t("basics.firstName")}
						/>
						<FormTextField
							errorMessage={errors?.lastName?.message}
							formRegister={register("lastName")}
							label={t("basics.lastName")}
						/>
					</div>
					<FormTextField
						errorMessage={errors?.email?.message}
						formRegister={register("email")}
						label={t("basics.email")}
						startAdornment={
							<InputAdornment position="start">
								<EmailOutlined />
							</InputAdornment>
						}
					/>
					<LanguageSelect
						currentLanguage={formValues.userLanguage}
						handleLanguageChange={handleLanguageChange}
						languages={languages}
					/>
				</Box>
				<div className="flex w-full justify-between px-6 ">
					<Button
						className="max-w-fit"
						color={ButtonColor.ACTION}
						title={ts("changePassword")}
						type="button"
						onClick={() => {
							setShowPasswordModal(true);
						}}
					/>

					<Box
						sx={{
							display: "flex",
							gap: "12px",
						}}
					>
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title={ts("cancel")}
							type="button"
							onClick={() => {
								reset(initialValues);
							}}
						/>
						<Button
							color={ButtonColor.ACTION}
							disabled={!isDirty}
							title={isLoading ? ts("saving") : ts("save")}
							type="submit"
						/>
					</Box>
				</div>
			</Box>
			<ProfileSecurity
				is2FAEnabled={user?.is2FAEnabled ?? false}
				isEmailOtpEnabled={user?.isEmailOtpEnabled ?? false}
			/>
			{/* Only a Global Admin can connect an AI client over MCP (BCM360-383). */}
			{isGlobalAdmin && <McpConnections />}
			{showPasswordModal && (
				<ChangePasswordModal
					handleClose={() => setShowPasswordModal(false)}
					isVisible={showPasswordModal}
				/>
			)}
		</>
	);
};
